export type DeprecatedInterestSuggestion = {
  deprecated: { id: string; name: string };
  alternative: { id: string; name: string } | null;
};

export type DeprecatedInterestSuggestionsResult = {
  suggestions: DeprecatedInterestSuggestion[];
  userTitle?: string;
  userMessage?: string;
};

const DEPRECATED_INTEREST_SUBCODE = 1870247;

const collectCandidateErrors = (source: any): any[] => {
  const results: any[] = [];
  const visited = new Set<any>();

  const visit = (value: any) => {
    if (!value || typeof value !== 'object' || visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    const asRecord = value as Record<string, unknown>;
    const looksLikeError =
      'error_subcode' in asRecord ||
      'error_user_msg' in asRecord ||
      'error_user_title' in asRecord ||
      ('code' in asRecord && 'message' in asRecord);

    if (looksLikeError) results.push(asRecord);

    Object.values(asRecord).forEach(visit);
  };

  visit(source);
  return results;
};

const extractAlternativesFromMessage = (
  message: unknown
): DeprecatedInterestSuggestion[] => {
  if (typeof message !== 'string') return [];

  const bracketStart = message.indexOf('[');
  const bracketEnd = message.lastIndexOf(']');
  if (bracketStart === -1 || bracketEnd <= bracketStart) return [];

  const jsonSlice = message.slice(bracketStart, bracketEnd + 1);

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonSlice);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  return parsed.flatMap((entry): DeprecatedInterestSuggestion[] => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;

    const deprecatedId = record.deprecated_interest_id;
    const deprecatedName = record.deprecated_interest_name;
    if (!deprecatedId) return [];

    const alternativeId = record.alternative_interest_id;
    const alternativeName = record.alternative_interest_name;

    return [
      {
        deprecated: {
          id: String(deprecatedId),
          name:
            typeof deprecatedName === 'string' && deprecatedName.trim()
              ? deprecatedName
              : String(deprecatedId)
        },
        alternative: alternativeId
          ? {
              id: String(alternativeId),
              name:
                typeof alternativeName === 'string' && alternativeName.trim()
                  ? alternativeName
                  : String(alternativeId)
            }
          : null
      }
    ];
  });
};

const dedupeSuggestions = (
  suggestions: DeprecatedInterestSuggestion[]
): DeprecatedInterestSuggestion[] => {
  const byId = new Map<string, DeprecatedInterestSuggestion>();
  suggestions.forEach((suggestion) => {
    if (!byId.has(suggestion.deprecated.id)) {
      byId.set(suggestion.deprecated.id, suggestion);
    }
  });
  return Array.from(byId.values());
};

export const parseDeprecatedInterestSuggestions = (
  source: any
): DeprecatedInterestSuggestionsResult | null => {
  if (!source) return null;

  if (typeof source === 'string') {
    const uniqueSuggestions = dedupeSuggestions(
      extractAlternativesFromMessage(source)
    );
    if (uniqueSuggestions.length === 0) return null;
    return { suggestions: uniqueSuggestions, userMessage: source };
  }

  const candidates = collectCandidateErrors(source);
  const matches = candidates.filter((candidate) => {
    const subcode = Number(candidate.error_subcode ?? candidate.subcode);
    return subcode === DEPRECATED_INTEREST_SUBCODE;
  });

  if (matches.length === 0) return null;

  const suggestions: DeprecatedInterestSuggestion[] = [];
  let userTitle: string | undefined;
  let userMessage: string | undefined;

  matches.forEach((match) => {
    if (!userTitle && typeof match.error_user_title === 'string') {
      userTitle = match.error_user_title;
    }
    const message =
      typeof match.error_user_msg === 'string'
        ? match.error_user_msg
        : typeof match.message === 'string'
        ? match.message
        : undefined;
    if (!userMessage && typeof message === 'string') {
      userMessage = message;
    }
    suggestions.push(...extractAlternativesFromMessage(message));
  });

  const uniqueSuggestions = dedupeSuggestions(suggestions);
  if (uniqueSuggestions.length === 0) return null;

  return {
    suggestions: uniqueSuggestions,
    userTitle,
    userMessage
  };
};

export const filterSuggestionsForInterests = (
  suggestions: DeprecatedInterestSuggestion[],
  interestIds: Iterable<string>
): DeprecatedInterestSuggestion[] => {
  const selected = new Set<string>();
  for (const id of interestIds) selected.add(String(id));
  return suggestions.filter((suggestion) =>
    selected.has(suggestion.deprecated.id)
  );
};
